// ===================================================================
// SHELTER — Incident Reports
// Shelter incident logging with severity, resolution tracking
// ===================================================================

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select, Textarea,
  Modal, Stack, SimpleGrid, Table, MultiSelect, ActionIcon, Drawer, Divider,
} from '@mantine/core';
import {
  IconSearch, IconPlus, IconPencil, IconRefresh, IconCheck,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useShelterStore } from '../../store/shelterStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { INCIDENT_REPORT_TYPES } from '../../types/shelter';
import type { ShelterIncident, IncidentSeverity } from '../../types/shelter';

const SEVERITY_COLORS: Record<IncidentSeverity, string> = {
  low: 'blue', medium: 'yellow', high: 'orange', critical: 'red',
};

const SEVERITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const EMPTY: Partial<ShelterIncident> = {
  shelterId: '', reportType: 'other', severity: 'low',
  description: '', involvedPartyIds: '[]', respondingStaff: '',
  resolution: '', notes: '',
};

export default function Incidents() {
  const { activeIncident, user } = useAuthStore();
  const { canEdit } = useModulePermission('shelter');
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    shelterIncidents, shelters, survivors,
    loadingIncidents,
    loadShelterIncidents, loadShelters, loadSurvivors,
    createShelterIncident, updateShelterIncident,
  } = useShelterStore();

  const [search, setSearch] = useState('');

  const [sortField, setSortField] = useState<string>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const sortIcon = (f: string) => sortField === f ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';
  const [shelterFilter, setShelterFilter] = useState<string | null>(null);
  const [severityFilter, setSeverityFilter] = useState<string | null>(null);
  const [resolvedFilter, setResolvedFilter] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ShelterIncident | null>(null);
  const [form, setForm] = useState<Partial<ShelterIncident>>({ ...EMPTY });
  const [involvedIds, setInvolvedIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [detailTarget, setDetailTarget] = useState<ShelterIncident | null>(null);

  useEffect(() => {
    loadShelterIncidents(); loadShelters(); loadSurvivors();
  }, [loadShelterIncidents, loadShelters, loadSurvivors]);

  // Store already server-side filters by active incident; use directly
  const iIncidents = incidentId ? shelterIncidents : [];
  const iShelters = incidentId ? shelters : [];
  const iSurvivors = incidentId ? survivors : [];

  const shelterName = (id: string) => shelters.find(s => String(s.id) === id)?.name ?? id;
  const reportTypeLabel = (val: string) => INCIDENT_REPORT_TYPES.find(t => t.value === val)?.label ?? val;

  const shelterSelectData = useMemo(() => iShelters.map(s => ({ value: String(s.id), label: s.name })), [iShelters]);
  const survivorSelectData = useMemo(() => iSurvivors.map(s => ({ value: String(s.id), label: `${s.firstName} ${s.lastName}` })), [iSurvivors]);

  const filtered = useMemo(() => {
    let list = iIncidents;
    if (shelterFilter && shelterFilter !== 'all') list = list.filter(i => i.shelterId === shelterFilter);
    if (severityFilter && severityFilter !== 'all') list = list.filter(i => i.severity === severityFilter);
    if (resolvedFilter === 'resolved') list = list.filter(i => !!i.resolvedAt);
    if (resolvedFilter === 'unresolved') list = list.filter(i => !i.resolvedAt);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(i =>
        i.description.toLowerCase().includes(q) ||
        i.reportType.toLowerCase().includes(q) ||
        i.respondingStaff.toLowerCase().includes(q),
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (!sortField) return (b.id || 0) - (a.id || 0);
      const va = (a as any)[sortField] ?? '';
      const vb = (b as any)[sortField] ?? '';
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [iIncidents, shelterFilter, severityFilter, resolvedFilter, search, sortField, sortDir]);

  const setField = (key: keyof ShelterIncident, value: string) => setForm(prev => ({ ...prev, [key]: value }));

  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...EMPTY });
    setInvolvedIds([]);
    setModalOpen(true);
  };

  const openEdit = (i: ShelterIncident) => {
    setEditTarget(i);
    setForm({ ...i });
    try { setInvolvedIds(JSON.parse(i.involvedPartyIds)); } catch { setInvolvedIds([]); }
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.description?.trim()) {
      notifications.show({ message: 'Description is required', color: 'yellow' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        incidentId,
        involvedPartyIds: JSON.stringify(involvedIds),
        reportedAt: editTarget ? form.reportedAt : new Date().toISOString(),
        reportedBy: editTarget ? form.reportedBy : (user?.fullName ?? ''),
      };

      if (editTarget) {
        await updateShelterIncident(editTarget.id, payload);
        notifications.show({ message: 'Incident updated', color: 'green' });
      } else {
        await createShelterIncident(payload);
        notifications.show({ message: 'Incident reported', color: 'green' });
      }
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally { setSaving(false); }
  };

  const handleResolve = async (i: ShelterIncident) => {
    try {
      await updateShelterIncident(i.id, {
        resolvedAt: new Date().toISOString(),
        resolution: i.resolution || 'Resolved',
      });
      notifications.show({ message: 'Incident resolved', color: 'green' });
    } catch {
      notifications.show({ message: 'Failed to resolve', color: 'red' });
    }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Incident Reports</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingIncidents} onClick={() => loadShelterIncidents()}>Refresh</Button>
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Report Incident</Button>}
        </Group>
      </Group>

      <Group gap="xs" mb="md">
        <TextInput size="xs" placeholder="Search..." leftSection={<IconSearch size={14} />}
          value={search} onChange={e => setSearch(e.currentTarget.value)} style={{ flex: 1 }} />
        <Select size="xs" placeholder="Shelter" data={[{ value: 'all', label: 'All Shelters' }, ...shelterSelectData]}
          value={shelterFilter} onChange={setShelterFilter} clearable w={180} />
        <Select size="xs" placeholder="Severity" data={[{ value: 'all', label: 'All Severities' }, ...SEVERITY_OPTIONS]}
          value={severityFilter} onChange={setSeverityFilter} clearable w={140} />
        <Select size="xs" placeholder="Status" data={[
          { value: 'all', label: 'All' },
          { value: 'resolved', label: 'Resolved' },
          { value: 'unresolved', label: 'Unresolved' },
        ]} value={resolvedFilter} onChange={setResolvedFilter} clearable w={140} />
      </Group>

      <Paper withBorder radius="md">
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Shelter</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('severity')}>Severity{sortIcon('severity')}</Table.Th>
              <Table.Th>Description</Table.Th>
              <Table.Th>Reported By</Table.Th>
              <Table.Th>Date</Table.Th>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('status')}>Status{sortIcon('status')}</Table.Th>
              <Table.Th w={80}>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filtered.length === 0 && (
              <Table.Tr><Table.Td colSpan={8}><Text c="dimmed" ta="center" py="lg">No incidents</Text></Table.Td></Table.Tr>
            )}
            {filtered.map(i => (
              <Table.Tr key={i.id} onClick={() => setDetailTarget(i)} style={{ cursor: 'pointer' }}>
                <Table.Td>{i.shelterId ? shelterName(i.shelterId) : '-'}</Table.Td>
                <Table.Td><Badge size="xs" variant="light">{reportTypeLabel(i.reportType)}</Badge></Table.Td>
                <Table.Td>
                  <Badge size="xs" color={SEVERITY_COLORS[i.severity]}>{i.severity}</Badge>
                </Table.Td>
                <Table.Td maw={250} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {i.description}
                </Table.Td>
                <Table.Td>{i.reportedBy || '-'}</Table.Td>
                <Table.Td>{i.reportedAt ? new Date(i.reportedAt).toLocaleDateString() : '-'}</Table.Td>
                <Table.Td>
                  {i.resolvedAt ? (
                    <Badge size="xs" color="green">Resolved</Badge>
                  ) : (
                    <Badge size="xs" color="red" variant="light" style={{ cursor: 'pointer' }}
                      onClick={() => handleResolve(i)}>Unresolved</Badge>
                  )}
                </Table.Td>
                {canEdit && (
                <Table.Td onClick={e => e.stopPropagation()}>
                  <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(i)}>
                    <IconPencil size={14} />
                  </ActionIcon>
                </Table.Td>
                )}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>

      <Text size="xs" c="dimmed" mt="xs">{filtered.length} incident{filtered.length !== 1 ? 's' : ''}</Text>

      {/* Detail Drawer */}
      <Drawer opened={!!detailTarget} onClose={() => setDetailTarget(null)} position="right" size="md"
        title={<Text fw={700} size="lg">Incident Details</Text>}>
        {detailTarget && (() => {
          let involvedNames: string[] = [];
          try {
            const ids: string[] = JSON.parse(detailTarget.involvedPartyIds);
            involvedNames = ids.map(id => {
              const s = iSurvivors.find(sv => String(sv.id) === id);
              return s ? `${s.firstName} ${s.lastName}` : id;
            });
          } catch { /* ignore */ }
          return (
            <Stack gap="md">
              <Group>
                <Badge variant="light">{reportTypeLabel(detailTarget.reportType)}</Badge>
                <Badge color={SEVERITY_COLORS[detailTarget.severity]}>{detailTarget.severity}</Badge>
                {detailTarget.resolvedAt
                  ? <Badge color="green">Resolved</Badge>
                  : <Badge color="red" variant="light">Unresolved</Badge>
                }
              </Group>

              <Divider />

              <div>
                <Text size="xs" c="dimmed">Shelter</Text>
                <Text size="sm">{detailTarget.shelterId ? shelterName(detailTarget.shelterId) : '-'}</Text>
              </div>

              <div>
                <Text size="xs" c="dimmed">Description</Text>
                <Text size="sm">{detailTarget.description || '-'}</Text>
              </div>

              {involvedNames.length > 0 && (
                <div>
                  <Text size="xs" c="dimmed">Involved Parties</Text>
                  <Text size="sm">{involvedNames.join(', ')}</Text>
                </div>
              )}

              <div>
                <Text size="xs" c="dimmed">Responding Staff</Text>
                <Text size="sm">{detailTarget.respondingStaff || '-'}</Text>
              </div>

              {detailTarget.resolution && (
                <div>
                  <Text size="xs" c="dimmed">Resolution</Text>
                  <Text size="sm">{detailTarget.resolution}</Text>
                </div>
              )}

              <div>
                <Text size="xs" c="dimmed">Follow-up Needed</Text>
                <Badge size="sm" color={detailTarget.followUpNeeded ? 'yellow' : 'gray'}>
                  {detailTarget.followUpNeeded ? 'Yes' : 'No'}
                </Badge>
              </div>

              <SimpleGrid cols={2}>
                <div>
                  <Text size="xs" c="dimmed">Reported At</Text>
                  <Text size="sm">{detailTarget.reportedAt ? new Date(detailTarget.reportedAt).toLocaleString() : '-'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Reported By</Text>
                  <Text size="sm">{detailTarget.reportedBy || '-'}</Text>
                </div>
              </SimpleGrid>

              {detailTarget.resolvedAt && (
                <div>
                  <Text size="xs" c="dimmed">Resolved At</Text>
                  <Text size="sm">{new Date(detailTarget.resolvedAt).toLocaleString()}</Text>
                </div>
              )}

              {detailTarget.notes && (
                <div>
                  <Text size="xs" c="dimmed">Notes</Text>
                  <Text size="sm">{detailTarget.notes}</Text>
                </div>
              )}

              <Divider />

              {canEdit && (
              <Group justify="flex-end">
                {!detailTarget.resolvedAt && (
                  <Button variant="light" color="green" leftSection={<IconCheck size={14} />}
                    onClick={() => { const t = detailTarget; setDetailTarget(null); handleResolve(t); }}>
                    Resolve
                  </Button>
                )}
                <Button variant="light" leftSection={<IconPencil size={14} />}
                  onClick={() => { const t = detailTarget; setDetailTarget(null); openEdit(t); }}>
                  Edit
                </Button>
              </Group>
              )}
            </Stack>
          );
        })()}
      </Drawer>

      {/* Create / Edit Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)} title={editTarget ? 'Edit Incident' : 'Report Incident'} size="lg">
        <Stack gap="sm">
          <SimpleGrid cols={2}>
            <Select label="Shelter" data={shelterSelectData} value={form.shelterId ?? ''}
              onChange={v => setField('shelterId', v ?? '')} required />
            <Select label="Report Type" data={INCIDENT_REPORT_TYPES.map(t => ({ value: t.value, label: t.label }))}
              value={form.reportType ?? 'other'} onChange={v => setField('reportType', v ?? 'other')} required />
          </SimpleGrid>
          <Select label="Severity" data={SEVERITY_OPTIONS}
            value={form.severity ?? 'low'} onChange={v => setField('severity', v ?? 'low')} required />
          <Textarea label="Description" rows={4} value={form.description ?? ''}
            onChange={e => setField('description', e.currentTarget.value)} required />
          <MultiSelect label="Involved Parties" data={survivorSelectData}
            value={involvedIds} onChange={setInvolvedIds} searchable clearable />
          <TextInput label="Responding Staff" value={form.respondingStaff ?? ''}
            onChange={e => setField('respondingStaff', e.currentTarget.value)} />
          {editTarget && (
            <Textarea label="Resolution" rows={3} value={form.resolution ?? ''}
              onChange={e => setField('resolution', e.currentTarget.value)} />
          )}
          <Textarea label="Notes" rows={2} value={form.notes ?? ''}
            onChange={e => setField('notes', e.currentTarget.value)} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>{editTarget ? 'Update' : 'Report'}</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
